import { Metric } from '@fullstory/proto/fs/api/metrics';
import {
  SpecObjectMapping,
  SpecTargetObjectType,
} from '@fullstory/proto/fs/services/search-settings';
import { BySlug, SpecObjectInfo } from '@fullstory/redux';
import { Event, Retention, SegmentDefinition } from '@fullstory/proto/fs/api/segment';
import { isNonNull } from '@fullstory/typeutils';
import { dependentEventFilterSlugs, eventFilterSlugs } from '@fullstory/util/spec';

// for spec, this object should be mixed into anything that needs it's data
export interface SpecAnalytics {
  // is spec present at all?
  specData_bool: boolean;
  // the object slugs that are in use
  specObjectSlugs_strs: string[];
  // the object types (page, element, defined event)
  specObjectTypes_strs: string[];
}

export const DEFAULT_SPEC_ANALYTICS: Readonly<SpecAnalytics> = Object.freeze({
  specData_bool: false,
  specObjectSlugs_strs: [],
  specObjectTypes_strs: [],
});

export function getMetricSpecProperties(
  metric: Metric | undefined | null,
  specObjectsBySlug: BySlug<SpecObjectInfo>,
): SpecAnalytics {
  if (!metric) return DEFAULT_SPEC_ANALYTICS;
  const events: Event[] = [];
  if (metric.single) {
    if (metric.single.event) events.push(metric.single.event);
  } else if (metric.dual) {
    if (metric.dual.first?.event) events.push(metric.dual.first.event);
    if (metric.dual.second?.event) events.push(metric.dual.second.event);
  }

  return eventsToSpecAnalytics(events, specObjectsBySlug);
}

export function getRetentionSpecProperties(
  retention: Retention | null | undefined,
  specObjectsBySlug: BySlug<SpecObjectInfo>,
): SpecAnalytics {
  return eventsToSpecAnalytics(
    [retention?.entry, ...(retention?.returns ?? [])].filter(isNonNull),
    specObjectsBySlug,
  );
}

export function getSegmentSpecProperties(
  segmentDefinition: SegmentDefinition | null | undefined,
  specObjectsBySlug: BySlug<SpecObjectInfo>,
): SpecAnalytics {
  const events = segmentDefinition?.behaviors.map(d => d.event).filter(isNonNull) ?? [];
  const excludeEvents =
    segmentDefinition?.excludeBehaviors.map(d => d.event).filter(isNonNull) ?? [];
  return eventsToSpecAnalytics(events.concat(...excludeEvents), specObjectsBySlug);
}

export function eventsToSpecAnalytics(
  events: Event[] | null | undefined,
  specObjectsBySlug: BySlug<SpecObjectInfo>,
): SpecAnalytics {
  const specAnalytics: SpecAnalytics = {
    ...DEFAULT_SPEC_ANALYTICS,
    specObjectSlugs_strs: [],
    specObjectTypes_strs: [],
  };
  if (!events) return specAnalytics;

  for (const event of events) {
    const specMappings = eventToMapping(event, specObjectsBySlug);
    if (!specMappings.length) continue;
    specAnalytics.specData_bool = true;
    for (const specMapping of specMappings) {
      specAnalytics.specObjectSlugs_strs.push(specMapping.objectSlug);
      specAnalytics.specObjectTypes_strs.push(
        getSpecTargetTypeString(specMapping.targetObjectType),
      );
    }
  }

  return specAnalytics;
}

export function getSpecTargetTypeString(specTarget: SpecTargetObjectType) {
  switch (specTarget) {
    case SpecTargetObjectType.SPEC_TARGET_OBJECT_TYPE_DEFINED_EVENT:
      return 'defined_event';
    case SpecTargetObjectType.SPEC_TARGET_OBJECT_TYPE_ELEMENT:
      return 'element';
    case SpecTargetObjectType.SPEC_TARGET_OBJECT_TYPE_API_EVENT:
      return 'api_event';
    case SpecTargetObjectType.SPEC_TARGET_OBJECT_TYPE_PAGE:
      return 'page';
    case SpecTargetObjectType.SPEC_TARGET_OBJECT_TYPE_INVALID:
    default:
      return '';
  }
}

export function eventToMapping(
  event: Event,
  specObjects: BySlug<SpecObjectInfo>,
): SpecObjectMapping[] {
  const objectMappings: SpecObjectMapping[] = [];
  if (event.primary) {
    const slugs = eventFilterSlugs(event.primary);
    objectMappings.push(...slugs.map(s => specObjects[s]?.mapping).filter(isNonNull));
  }
  if (event.deps) {
    event.deps.forEach(dep => {
      const slugs = dependentEventFilterSlugs(dep);
      objectMappings.push(...slugs.map(s => specObjects[s]?.mapping).filter(isNonNull));
    });
  }
  return objectMappings;
}
